import { version } from '../../package.json';
import buildConfig from '../build-config.json';
import { DATA_UPLOAD_INTERVAL, DEFAULT_RECONFIGURE_INTERVAL } from '../constants';
import { PrebuildMessage } from '../message.types';
import { SilverEggSetting } from '../recommendation.types';
import { UngroupedTrackingRule } from '../ungrouped-tracking-rule.types';
import { fetch } from '../utility/fetch';
import { HTTP_STATUS } from './constants';
import { ReproWebInsightSchema as ResponseJSON } from '../generated/repro.rails/v1-internal-web-config';
import { SpaMode } from '../spamode';

interface Params {
  userAnnotation?: string;
  idfv: string;
  token: string;
  previewToken?: string;
}

type optionsFromServer = Exclude<Exclude<ResponseJSON, number>['sdk_config'], undefined>;
type typedOptions = Partial<{ spa_mode?: SpaMode } & Omit<optionsFromServer, 'spa_mode'>>;

export interface Configuration {
  messages: PrebuildMessage[];
  trackingRules: UngroupedTrackingRule[];
  send_errors: boolean;
  pushSetting?: {
    serviceWorkerUrl: string;
    serviceWorkerScope: string;
    vapidKey: number[];
  } | null;
  appId: number;
  insightId: number;
  trackable: boolean;
  ipAddress: string;
  timeOffsetMills: number;
  dataUploadInterval: number;
  reconfigureInterval: number;
  inspect_mode?: {
    idfv: string;
  } | null;
  silverEggSetting: SilverEggSetting;
  googleAnalyticsTrackingCode: string;
  setupOptions?: typedOptions;
}

const isKeyOfParams = (prop: string): prop is keyof Params => ['userAnnotation', 'idfv', 'token'].includes(prop);

const isSpaMode = (mode?: string): mode is SpaMode => !!mode && Object.values(SpaMode).includes(mode as SpaMode);

const filterOptionValue = (inputOptions?: Readonly<optionsFromServer>): typedOptions | undefined => {
  if (typeof inputOptions === 'undefined') return undefined;
  const options = {
    ...inputOptions,
    spa_mode: isSpaMode(inputOptions.spa_mode) ? inputOptions.spa_mode : undefined,
  };
  Object.entries(options).forEach(([k, v]) => {
    if (typeof v === 'undefined') delete options[k as keyof typeof options];
  });
  return options;
};

export const fetchConfiguration = async (params: Params): Promise<Configuration> => {
  if (['idfv', 'token'].some((prop) => isKeyOfParams(prop) && params[prop] === undefined)) {
    return Promise.reject('Aborting Configuration API call due to idfv or token missing.');
  }

  if (!navigator.onLine) {
    // この場合は trackable: false にするのではなく、trackしたイベントを cache storage かどこかに置いておいて、
    // 次の config成功時 or オンラインに復帰したときにcustom_eventsにマージできればオフライン対応完了か？（希望的観測）
    // オフライン対応時には trackable だけじゃなくオフライン時の状態も保持する必要あるが、詳細は追って考える
    // https://repro.esa.io/posts/13891#comment-511496
    return Promise.reject(new Error('Aborting configuration API call due to browser being offline.'));
  }

  const { userAnnotation: user_annotation = '', idfv, token, previewToken } = params;

  const requestParams = {
    user_annotation,
    idfv,
    token,
    sdk_version: version,
    os: 'web',
    preview_token: previewToken,
  };

  const response = await fetch(buildConfig.apiConfigURL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(requestParams),
    mode: 'cors',
  });
  // TODO: HTTP と HTTPS で configurable にする?
  if (response.status === HTTP_STATUS.UNAUTHORIZED) {
    throw new Error('Configuration API call failed due to authentication error. Check your repro token.');
  }
  if (!response.ok) {
    throw new Error(`Configuration API call failed with status ${response.status}.`);
  }
  const json: ResponseJSON = await response.json();

  if (typeof json !== 'object') {
    throw new Error(`Invalid config response: ${json}`);
  }

  const configuration: Configuration = {
    messages: json.web_messages || [],
    trackingRules: json.tracking_rules || [],
    send_errors: json.send_errors ?? true,
    // config APIでもらう responded_at は時刻精度が低く、そのままJavaScriptの Date オブジェクトと比較や演算をすると
    // 意図しない時刻になってしまうので、 000 をパディングした epoch time として保持する
    appId: json.insight.app_id,
    insightId: json.insight.id,
    timeOffsetMills: Date.now() - parseInt(`${json.responded_at}000`, 10),
    trackable: json.insight.trackable,
    ipAddress: json.insight.ip_address,
    dataUploadInterval: json.insight.data_upload_interval ?? DATA_UPLOAD_INTERVAL,
    reconfigureInterval: json.insight.reconfigure_interval ?? DEFAULT_RECONFIGURE_INTERVAL,
    silverEggSetting: {
      merchantId: json.insight.silver_egg.merchant_id !== null ? json.insight.silver_egg.merchant_id : '',
      domain: json.insight.silver_egg.domain !== null ? json.insight.silver_egg.domain : '',
    },
    googleAnalyticsTrackingCode: '', // TODO: 現状 API レスポンスに含まれていないので、ここでコードが入ることはない。将来的にトラッキングIDをセットできるようにする場合、API 応答とともに変更する
    setupOptions: filterOptionValue(json.sdk_config),
  };

  if (json.push_setting != null) {
    configuration.pushSetting = {
      serviceWorkerUrl: json.push_setting.service_worker_url,
      serviceWorkerScope: json.push_setting.service_worker_scope,
      vapidKey: json.push_setting.vapid_key,
    };
  }

  if (json.inspect_mode != null) {
    configuration.inspect_mode = json.inspect_mode;
  }

  return configuration;
};
